import React from 'react';
import { formatDate, getItemId, getMappedValueFromOrderOrItem, parseUTCDate, priceFormat } from '../../../../util';
import * as toolSelectors from '../../../../selectors/tools';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { getItemNameAndQuantity } from '../helpers';
import { TrackingLinkOrInfo } from '../Common/TrackingLinkOrInfo/TrackingLinkOrInfo';
import {
  ResultsColumn,
  ResultsColumnHeader,
  Row,
  HeaderRow,
  SubTitleText,
  Spreadsheet,
  TitleRow,
  ColumnTitle,
  TitleText,
  NotLastItem,
  LastItem,
  Column,
  MultipleLineContainer,
} from '../DTInfoWidget.styles';

const ItemRow = (props) => {
  const { name, price, shippingStatus, dates, tracking } = props;

  return (
    <Row className="fs-block">
      <ResultsColumn>{name}</ResultsColumn>
      <ResultsColumn>{price}</ResultsColumn>
      <ResultsColumn>{shippingStatus}</ResultsColumn>
      <ResultsColumn>
        <MultipleLineContainer>
          {dates.map((date, i) => (
            <span key={i}>{date}</span>
          ))}
        </MultipleLineContainer>
      </ResultsColumn>
      <ResultsColumn>{tracking}</ResultsColumn>
    </Row>
  );
};

const ListHeader = (props) => {
  const { title, dateColumnTitle } = props;

  return (
    <>
      <SubTitleText>{title}</SubTitleText>
      <HeaderRow>
        <ResultsColumnHeader>ITEM</ResultsColumnHeader>
        <ResultsColumnHeader>PRICE</ResultsColumnHeader>
        <ResultsColumnHeader>SHIPPING STATUS</ResultsColumnHeader>
        <ResultsColumnHeader>{dateColumnTitle}</ResultsColumnHeader>
        <ResultsColumnHeader>TRACKING</ResultsColumnHeader>
      </HeaderRow>
    </>
  );
};

const DTOrderStatusLineItemDetailsWidget = () => {
  const selectedLineItems = useSelector(toolSelectors.getSelectedLineItems);
  const orderingSystem = useSelector(toolSelectors.getOrderingSystem);
  const selectedOrder = useSelector(toolSelectors.getSelectedOrder);
  const trackingStatusByItemId = useSelector(toolSelectors.getTrackingStatusByItemId);
  const statusOfSelectedItems = useSelector(toolSelectors.getStatusOfItemsOfSelectedOrderByItemId);

  // // TODO: compare this function with with the similar one in /selectors/tools, line 1182. To be refactored later.
  const getOrderId = () => {
    switch (orderingSystem) {
      case 'common':
        return get(selectedOrder, 'orderNumber');
      case 'shopify':
        return get(selectedOrder, 'order_number');
      case 'bigCommerce':
        return get(selectedOrder, 'id');
      default:
        return null;
    }
  };

  const getLastUpdated = (item) => {
    // TODO: Add default case
    // eslint-disable-next-line
    switch (orderingSystem) {
      case 'common':
        const trackingInformation = get(trackingStatusByItemId, `[${getItemId(item, orderingSystem)}]`, []);

        if (isEmpty(trackingInformation)) {
          return ['no update'];
        }

        return trackingInformation.map((tracking) => {
          if (tracking?.statusUpdatedAt) {
            const date = tracking.statusUpdatedAt;
            if (date) {
              return formatDate(parseUTCDate(date));
            }
          }

          return 'no update';
        });
      case 'bigCommerce':
        const shippoInfo = get(trackingStatusByItemId, `[${getItemId(item, orderingSystem)}]`, []);

        return shippoInfo.map((tracking) => {
          // check we actually have tracking status on the object
          if (tracking?.tracking_status) {
            const date = tracking.tracking_status.status_date;
            if (date) {
              return formatDate(parseUTCDate(date));
            }
          }

          // default to no update
          return 'no update';
        });
      case 'shopify':
        const trackingInfo = get(trackingStatusByItemId, `[${item.id}]`, []);

        return trackingInfo.map((tracking) => {
          const date = tracking.updated_at;
          // check we actually have shipment status on the fulfillment object
          if (!tracking.shipment_status || !date) {
            return 'no update';
          }

          return formatDate(parseUTCDate(date));
        });
      default:
        return [];
    }
  };

  const getShippingStatus = (item) => {
    switch (orderingSystem) {
      case 'common':
        // Items almost always have one shipment
        return get(item, 'shipments[0].shippingStatus') || get(item, 'itemShippingStatus');
      case 'shopify':
      case 'bigCommerce':
        return statusOfSelectedItems[item.id];
      default:
        return null;
    }
  };

  const itemsToRender = selectedLineItems.map((item) => ({
    ...item,
    itemName: getItemNameAndQuantity(item, orderingSystem),
    shippingStatus: getShippingStatus(item),
    itemPrice: priceFormat(getMappedValueFromOrderOrItem(item, 'price', orderingSystem)),
    itemDates: getLastUpdated(item),
    itemTracking: <TrackingLinkOrInfo item={item} />,
  }));
  const unfulfilledItems = itemsToRender.filter((item) => item.shippingStatus === 'unfulfilled');
  const fulfilledItems = itemsToRender.filter(
    (item) => item.shippingStatus !== 'unfulfilled' && item.shippingStatus !== 'delivered'
  );
  const deliveredItems = itemsToRender.filter((item) => item.shippingStatus === 'delivered');
  const orderDateOfSelectedOrder = useSelector(toolSelectors.getOrderDateOfSelectedOrder);
  const orderDate = formatDate(parseUTCDate(String(orderDateOfSelectedOrder)));
  const shippingDescriptionOfSelectedOrder = useSelector(toolSelectors.getShippingDescriptionOfSelectedOrder);

  return (
    <>
      {(getOrderId() || orderDateOfSelectedOrder) && (
        <TitleRow className="fs-block">
          <ColumnTitle>
            <TitleText>ORDER ID: {getOrderId()}</TitleText>
          </ColumnTitle>
          <Column>
            <NotLastItem>DATE OF ORDER: {orderDate}</NotLastItem>
            {shippingDescriptionOfSelectedOrder && <LastItem>SHIPPING: {shippingDescriptionOfSelectedOrder}</LastItem>}
          </Column>
        </TitleRow>
      )}

      {!isEmpty(deliveredItems) && (
        <Spreadsheet className="fs-block">
          <ListHeader title={'DELIVERED ITEMS'} dateColumnTitle={'DELIVERY DATE'} />
          {deliveredItems.map((item, idx) => (
            <ItemRow
              key={`deliveredItems_${idx}`}
              name={item.itemName}
              price={item.itemPrice}
              shippingStatus={item.shippingStatus}
              dates={item.itemDates}
              tracking={item.itemTracking}
            />
          ))}
        </Spreadsheet>
      )}

      {!isEmpty(fulfilledItems) && (
        <Spreadsheet className="fs-block">
          <ListHeader title={'FULFILLED ITEMS'} dateColumnTitle={'LAST UPDATED'} />
          {fulfilledItems.map((item, idx) => (
            <ItemRow
              key={`filledItems_${idx}`}
              name={item.itemName}
              price={item.itemPrice}
              shippingStatus={item.shippingStatus}
              dates={item.itemDates}
              tracking={item.itemTracking}
            />
          ))}
        </Spreadsheet>
      )}

      {!isEmpty(unfulfilledItems) && (
        <Spreadsheet className="fs-block">
          <ListHeader title={'UNFULFILLED ITEMS'} dateColumnTitle={'SHIP DATE'} />
          {unfulfilledItems.map((item, idx) => (
            <ItemRow
              key={`unfulfilledItems_${idx}`}
              name={item.itemName}
              price={item.itemPrice}
              shippingStatus={item.shippingStatus}
              dates={[item.shipping_date ? item.shipping_date : 'unavailable']}
              tracking={'unavailable'}
            />
          ))}
        </Spreadsheet>
      )}
    </>
  );
};
export default DTOrderStatusLineItemDetailsWidget;
